'use client';

import type { UserBike } from 'models/shared';
import { createContext, useContext, useState, type ReactNode } from 'react';

type FilteredBike = Partial<UserBike> & { allYears?: number[] };

type BikeFilterState = {
  bikeYears: string[];
  bikeMakes: string[];
  bikeModels: string[];
  selectedBike: FilteredBike;
};

type BikeFilterActions = {
  setBikeFilters: (filters: {
    years?: string[];
    makes?: string[];
    models?: string[];
  }) => void;
  setSelectedBike: (bike: FilteredBike) => void;
  clearBikeFilters: () => void;
  clearSelectedBike: () => void;
  clearAll: () => void;
};

type BikeFilterContextType = BikeFilterState & BikeFilterActions;

const BikeFilterContext = createContext<BikeFilterContextType | undefined>(
  undefined
);

const INITIAL_STATE: BikeFilterState = {
  bikeYears: [],
  bikeMakes: [],
  bikeModels: [],
  selectedBike: {
    year: undefined,
    make: undefined,
    model: undefined,
  },
};

export function BikeFilterProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BikeFilterState>(INITIAL_STATE);

  const setBikeFilters = (filters: {
    years?: string[];
    makes?: string[];
    models?: string[];
  }) => {
    setState((prev) => ({
      ...prev,
      bikeYears: filters.years ?? prev.bikeYears,
      bikeMakes: filters.makes ?? prev.bikeMakes,
      bikeModels: filters.models ?? prev.bikeModels,
    }));
  };

  const setSelectedBike = (bike: FilteredBike) => {
    setState((prev) => ({
      ...prev,
      selectedBike: {
        ...bike,
      },
    }));
  };

  const clearBikeFilters = () => {
    setState((prev) => ({
      ...prev,
      bikeYears: [],
      bikeMakes: [],
      bikeModels: [],
    }));
  };

  const clearSelectedBike = () => {
    setState((prev) => ({
      ...prev,
      selectedBike: {
        year: undefined,
        make: undefined,
        model: undefined,
      },
    }));
  };

  const clearAll = () => {
    setState(INITIAL_STATE);
  };

  const contextValue: BikeFilterContextType = {
    ...state,
    setBikeFilters,
    setSelectedBike,
    clearBikeFilters,
    clearSelectedBike,
    clearAll,
  };

  return (
    <BikeFilterContext.Provider value={contextValue}>
      {children}
    </BikeFilterContext.Provider>
  );
}

export function useBikeFilter() {
  const context = useContext(BikeFilterContext);
  if (context === undefined) {
    throw new Error('useBikeFilter must be used within a BikeFilterProvider');
  }
  return context;
}
